// @mui
import { Box, Container, Tabs, Tab, Divider, useTheme } from "@mui/material";
// routes
// hooks
import useSettings from "../../hooks/useSettings";
import useTabs from "../../hooks/useTabs";
// components
import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useLocation, useParams, Outlet, useNavigate } from "react-router-dom";
import { getBrand } from "~src/api/brands";
import LoadingScreen from "~src/components/LoadingScreen";
import BrandNotFound from "~src/components/BrandNotFound";
import { Brand } from "~src/types/brand";
import Page from "../../components/Page";
import { BrandHeader } from "./BrandHeader";
import { navItems } from "./navItems";
import useGetFullBrand from "~src/hooks/useGetFullBrand";
import { logInfo, logError } from "~src/utils/logger";

// Preload all tab pages to prevent flashing when first clicking on tabs
const preloadTabPages = () => {
  // Using dynamic imports to preload the components
  const preloadPromises = [
    import("~src/pages/brands/Detail/Overview"),
    import("~src/pages/brands/Detail/TrendViability"),
    import("~src/pages/brands/Detail/ReviewTracker"),
    import("~src/pages/brands/Detail/Social"),
    import("~src/pages/brands/Detail/GoogleAds"),
    import("~src/pages/brands/Detail/FbAds"),
    import("~src/pages/brands/Detail/TrafficAndRevenue"),
    import("~src/pages/brands/Detail/Products")
  ];
  
  // Execute all preloads in parallel
  Promise.all(preloadPromises).catch(err => 
    logInfo(`Tab preloading non-critical error: ${err}`)
  );
};

// ----------------------------------------------------------------------

export type BrandLayoutContext = {
  brand: Brand;
  isInitialLoad: boolean;
};

export default function BrandLayout() {
  const { themeStretch } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [brand, setBrand] = useState<Brand>();
  const [brandNotFound, setBrandNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { id } = useParams();

  // Track if we've already fetched brand data to avoid duplicate fetches
  const hasFetchedRef = useRef(false);

  // Reset fetch tracking when brand ID changes (for navigation between brands)
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [id]);

  // Get navigation state if available
  const navigationState = location.state as { brandData?: Brand } | undefined;
  
  // Use the full brand loading hook
  const fullBrandLoadingContext = useGetFullBrand(id ? parseInt(id) : 0, 30);

  // Determine the default tab based on the current URL path
  const defaultTab = useMemo(() => {
    const pathname = location.pathname;
    const currentTab = navItems.find((tab: { path: string; value: string }) => pathname.endsWith(tab.path));
    return currentTab ? currentTab.value : "Overview";
  }, [location.pathname]);

  // Use the useTabs hook for consistent tab management
  const { currentTab, onChangeTab } = useTabs(defaultTab);

  // Use navigation state data if available
  useEffect(() => {
    if (navigationState?.brandData) {
      setBrand(navigationState.brandData);
      setIsLoading(false);
      setBrandNotFound(false);
      hasFetchedRef.current = true;
    }
  }, [navigationState]);

  // Memoized function to fetch brand data to avoid unnecessary re-renders
  const fetchBrandData = useCallback(async () => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    // Skip API call if we already have brand data from navigation state or previous fetch
    if (hasFetchedRef.current) {
      return;
    }

    try {
      setIsLoading(true);
      setBrandNotFound(false);
      const result = await getBrand(parseInt(id));

      if (result) {
        setBrand(result);
        setBrandNotFound(false);
        hasFetchedRef.current = true;
        // Preload all tab components once we have the brand data
        preloadTabPages();
      } else {
        // Brand not found - API returned empty results
        setBrand(undefined);
        setBrandNotFound(true);
      }
    } catch (error) {
      logError(`Error fetching brand data: ${error}`);
      setBrand(undefined);
      setBrandNotFound(true);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  // Call fetchBrandData when component mounts
  useEffect(() => {
    fetchBrandData();
  }, [fetchBrandData]);

  // Navigate to the appropriate tab when tab changes
  useEffect(() => {
    const tab = navItems.find((item: { value: string; path: string }) => item.value === currentTab);
    if (tab && id) {
      const path = `/brands/${id}/${tab.path}`;
      if (location.pathname !== path) {
        // Remove setting transition states
        navigate(path, { replace: true });
      }
    }
  }, [currentTab, id, navigate, location.pathname]);

  // Show brand not found error if brand doesn't exist
  if (brandNotFound) {
    return <BrandNotFound />;
  }

  // Show a full-page loading state while fetching brand data
  if (isLoading || !brand || fullBrandLoadingContext.loading) {
    return <LoadingScreen />;
  }

  return (
    <Page title={brand.brandname}>
      <Container maxWidth={themeStretch ? false : "lg"} disableGutters>
        <Box>
          <BrandHeader 
            brand={brand} 
            fullBrandLoadingContext={fullBrandLoadingContext} 
          />
          {/* Tabs container with consistent padding */}
          <Box
            sx={{
              px: { xs: 2, sm: 4 }, // Horizontal padding that matches content
              mt: 2, // Add a little space after the header
            }}
          >
            <Tabs
              allowScrollButtonsMobile
              variant="scrollable"
              scrollButtons="auto"
              value={currentTab}
              onChange={onChangeTab}
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: theme.palette.primary.main,
                  height: 3
                }
              }}
            >
              {navItems.map((tab: { value: string; path: string }) => (
                <Tab
                  disableRipple
                  data-testid={tab.value}
                  key={tab.value}
                  label={tab.value}
                  value={tab.value}
                  sx={{
                    minHeight: 48,
                    textTransform: 'none',
                    '&.MuiTab-root': {
                      color: 'text.primary',
                    },
                    '&.Mui-selected': {
                      color: theme.palette.primary.main,
                      fontWeight: 600
                    }
                  }}
                />
              ))}
            </Tabs>
            <Divider
              sx={{
                borderColor: "grey.300",
              }}
            />
          </Box>
          
          {/* Content area with consistent padding */}
          <Box
            sx={{
              position: 'relative',
              minHeight: '500px',
              px: { xs: 2, sm: 4 }, // Use px instead of mx for consistent content alignment
              pt: 3, // Add top padding for spacing below tabs
              pb: 4, // Add bottom padding
            }}
          >
            <Outlet context={{ brand, isInitialLoad: false }} />
          </Box>
        </Box>
      </Container>
    </Page>
  );
}
